'use client';

import { H2 } from 'components/home/styleConstants';
import { H3, P } from './StyleComponents';
import { Container, Content } from './LayoutComponents';
import { CustomImage } from '..';
import { NudgeCard } from 'components/m1/NudgeCard';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useEffect, useRef } from 'react';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import styles from './MasterYourMetabolism.module.css';

const baseFeatures = [
  {
    title: 'Real-time glucose monitoring',
    description: 'Track your real-time glucose with just a quick scan.',
    img: 'web_v2/m1/features/glucose-monitor-latest.png',
    fullSize: false,
  },
  {
    title: 'Metabolic Score',
    description:
      'Visualize your metabolic health in real-time with Metabolic score.',
    img: 'web_v2/m1/features/metabolic-score-latest.png',
    fullSize: false,
  },
  {
    title: 'Food score',
    description:
      'Measure how different foods affect your glucose levels on a scale of 1 to 10. Find the foods that work for you and optimise the ones that don’t.',
    img: 'web_v2/m1/features/food-score-up.png',
    fullSize: false,
  },
  {
    title: 'Fuelling score',
    description:
      'Maximize your sports & workout performance using the fueling score. Understand foods and lifestyle habits that will help you improve performance & focus during activities.',
    img: 'web_v2/m1/features/fuelling-score-latest.png',
    fullSize: false,
  },
  {
    title: 'Metabolic nudges',
    description:
      'Get personalized and actionable nudges that help you make better health choices throughout the day.',
    img: 'web_v2/m1/features/nudge.1.png',
    fullSize: true,
  },
  {
    title: 'Science-backed coaching, on us',
    description:
      'Get personalized, science-backed insights into your glucose trends from our ASCM-certified coaches at no extra cost.',
    img: 'web_v2/m1/features/ultracare-m1-2.png',
    fullSize: false,
  },
  {
    title: 'One app. Many integrations.',
    description:
      'Automatically sync your workout, sleep, and menstrual data with top apps, including Apple Health, Google Fit, Strava, MyFitnessPal, Clue, and more.',
    img: 'web_v2/m1/features/app-integrations.png',
    fullSize: false,
  },
  {
    title: 'Community insights',
    description:
      'Understand how your food response stacks up against your fellow Cyborgs. ',
    img: 'web_v2/m1/features/community-insights-latest.png',
    fullSize: false,
  },
  {
    title: 'Trend correlations',
    description:
      'Uncover the patterns behind your metrics to see what drives improvement and performance.',
    img: 'web_v2/m1/features/correlations.png',
    fullSize: false,
  },
];

type FeatureCardProps = {
  title: string;
  description: string;
  img: string;
  fullSize?: boolean;
};

const FeatureCard = ({ feature }: { feature: FeatureCardProps }) => {
  const { title, description, img, fullSize } = feature;

  return (
    <div
      className={`${styles.featureCard}${
        fullSize ? ` ${styles['full-size']}` : ''
      }`}
    >
      <div className={styles.content}>
        <H3 align="left">{title}</H3>
        <P color="rgba(255, 255, 255, 0.5)" align="left">
          {description}
        </P>
      </div>
      <div className={styles['img-wrapper']}>
        {img ? (
          <CustomImage
            src={img}
            alt=""
            fill
            style={{ objectFit: 'contain' }}
          />
        ) : null}
      </div>
    </div>
  );
};

export const Video = ({ className }: { className?: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.8;
    }
  }, [videoRef]);

  return (
    <div
      className={`${styles.videoRoot}${className ? ` ${className}` : ''} ${
        styles['full-size']
      }`}
    >
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        className={styles.video}
        src={getCompressedAssetUrl('/web_v2/m1/space.mp4')}
      ></video>
      <div className={styles.bg}></div>
      <div className={styles['video-content']}>
        <h3 className="number">1.18 billion</h3>
        <h3 className={styles.description}>
          data points were captured through the CGM
        </h3>
        {/* <p>
          <span>4567</span> insights generated <br /> as you are scrolling
        </p> */}
      </div>
    </div>
  );
};

export const MasterYourMetabolism = ({
  className,
}: {
  className?: string;
}) => {
  const { store } = useCartStore(useShallow((s) => ({ store: s.store })));
  const features =
    store === ShopifyStore.US
      ? baseFeatures.map((feature, index) => {
          if (index === 0) {
            return {
              ...feature,
              title: 'Continuous glucose monitoring',
              description: 'Track your glucose with just a quick scan.',
            };
          }
          if (index === 5) {
            return {
              ...feature,
              title: 'Your AI metabolic coach, always on',
              description:
                'Jade, your AI coach, reads your glucose biomarker data in real time and answers your questions instantly — personalized guidance, any time of day, at no extra cost.',
              img: 'web_v2/m2/jade.png',
            };
          }
          return feature;
        })
      : baseFeatures;
  return (
    <Container
      fill="black"
      className={`${styles.root}${className ? ` ${className}` : ''}`}
    >
      <Content className={styles.content}>
        <H2>
          {store === ShopifyStore.US
            ? 'The M2 Live experience'
            : 'The M1 Live experience'}
        </H2>
        <div className={styles.features}>
          {features.map((feature, index) =>
            feature.fullSize ? (
              <div key={index} className={styles['full-size']}>
                <NudgeCard {...feature} />
              </div>
            ) : (
              <FeatureCard key={index} feature={feature} />
            ),
          )}
          <Video />
        </div>
      </Content>
    </Container>
  );
};
